// ABOUTME: Ban notification store and dialog component
// ABOUTME: Manages global state for displaying ban notifications to users

import { Ban, Clock, AlertTriangle, ShieldAlert } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { hidePenalty } from "@/shared/model/penalty-notification";
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalTitle,
} from "@/shared/ui/responsive-modal";

type BanInfo = {
  message: string;
  isPermanent: boolean;
  bannedUntil?: string;
};

type BanNotificationInternalState = {
  isOpen: boolean;
  banInfo: BanInfo | null;
};

const banNotificationStore = createBanNotificationStore();

function createBanNotificationStore() {
  let state: BanNotificationInternalState = {
    isOpen: false,
    banInfo: null,
  };

  const listeners = new Set<() => void>();

  function notifyListeners() {
    listeners.forEach((listener) => listener());
  }

  return {
    getState: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    showBan: (info: BanInfo) => {
      state = { isOpen: true, banInfo: info };
      notifyListeners();
    },
    hideBan: () => {
      state = { isOpen: false, banInfo: null };
      notifyListeners();
    },
  };
}

function useBanNotificationState() {
  const [state, setState] = React.useState(() =>
    banNotificationStore.getState(),
  );

  React.useEffect(() => {
    const unsubscribe = banNotificationStore.subscribe(() => {
      setState(banNotificationStore.getState());
    });
    return unsubscribe;
  }, []);

  return state;
}

export function showBan(info: BanInfo) {
  hidePenalty();
  banNotificationStore.showBan(info);
}

export function hideBan() {
  banNotificationStore.hideBan();
}

function useFormatBanDate() {
  const { t } = useTranslation();

  const formatBanDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.ceil(diffMs / (1000 * 60));

      const formattedDate = date.toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      if (diffMs <= 0) {
        return `${t("ban.time.expired")} (${formattedDate})`;
      }

      if (diffMinutes < 60) {
        return `${t("ban.time.in")} ${diffMinutes} ${pluralize(diffMinutes, t("ban.time.minutes_one"), t("ban.time.minutes_few"), t("ban.time.minutes_many"))} (${formattedDate})`;
      }

      if (diffHours < 24) {
        return `${t("ban.time.in")} ${diffHours} ${pluralize(diffHours, t("ban.time.hours_one"), t("ban.time.hours_few"), t("ban.time.hours_many"))} (${formattedDate})`;
      }

      if (diffDays <= 7) {
        return `${t("ban.time.in")} ${diffDays} ${pluralize(diffDays, t("ban.time.days_one"), t("ban.time.days_few"), t("ban.time.days_many"))} (${formattedDate})`;
      }

      return formattedDate;
    } catch {
      return dateString;
    }
  };

  return formatBanDate;
}

function pluralize(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;

  if (mod100 >= 11 && mod100 <= 19) {
    return many;
  }

  if (mod10 === 1) {
    return one;
  }

  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }

  return many;
}

function BanReasonsList() {
  const { t } = useTranslation();

  const reasons = [
    {
      title: t("ban.reasons.rules.title"),
      description: t("ban.reasons.rules.description"),
    },
    {
      title: t("ban.reasons.complaints.title"),
      description: t("ban.reasons.complaints.description"),
    },
    {
      title: t("ban.reasons.suspicious.title"),
      description: t("ban.reasons.suspicious.description"),
    },
    {
      title: t("ban.reasons.terms.title"),
      description: t("ban.reasons.terms.description"),
    },
    {
      title: t("ban.reasons.moderation.title"),
      description: t("ban.reasons.moderation.description"),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <ShieldAlert className="h-4 w-4" />
        <span>{t("ban.reasons.title")}</span>
      </div>
      <ul className="space-y-2">
        {reasons.map((reason, index) => (
          <li key={index} className="rounded-lg border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{reason.title}</p>
            <p className="text-muted-foreground">{reason.description}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BanNotificationDialog() {
  const { t } = useTranslation();
  const { isOpen, banInfo } = useBanNotificationState();
  const formatBanDate = useFormatBanDate();

  if (!banInfo) return null;

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) hideBan();
      }}
    >
      <ResponsiveModalContent showCloseButton={false} className="max-w-md">
        <ResponsiveModalHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <Ban className="h-8 w-8 text-destructive" />
          </div>
          <ResponsiveModalTitle className="text-xl">
            {t("ban.title")}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-6">
          {banInfo.isPermanent ? (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="font-medium text-destructive">
                  {t("ban.permanent.title")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("ban.permanent.description")}
                </p>
              </div>
            </div>
          ) : (
            banInfo.bannedUntil && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-600">
                    {t("ban.temporary.title")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("ban.temporary.description", {
                      date: formatBanDate(banInfo.bannedUntil),
                    })}
                  </p>
                </div>
              </div>
            )
          )}

          <BanReasonsList />
        </ResponsiveModalBody>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
